import { QueryClient, useMutation } from "@tanstack/react-query"
import {
    addFolder,
    archiveDataGeneral,
    deleteDataGeneral,
    deleteDocFromSingleType,
    deleteFolder,
    getDocumentFromAnyType,
    postNewDataGeneral,
    postNewDocumentInFolder,
    restoreData,
    startExcelExport,
    startZipExtraction,
} from "./apiCalls"
import { ArchiveDataParams, DeleteDataParams, PostNewDataParams } from "./Types/Types"
import { messageError, messagePending, messageSuccess } from "../../services/messages"
import { apiDisplayMessage, apiErrorMessage } from "../../services/apiErrorText"
import { useState } from "react"

// download document
export const useDownloadDocumentMutation = () => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<Blob, Error, { id: string; s3Key: string; filetype: "object" | "customer" | "employee" | "task" | "report" | "offer" | "credit" | "bill"; filename: string }>({
        mutationFn: getDocumentFromAnyType,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Dokument wird gerade heruntergeladen.")
        },
        onSuccess: (data, variables) => {
            const url = window.URL.createObjectURL(data)
            const a = document.createElement("a")
            a.href = url
            a.download = variables.filename
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            a.remove()
            messageSuccess(notificationKey, "Fertig!", "Das Dokument wurde erfolgreich heruntergeladen.")
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Dokument konnte nicht heruntergeladen werden."))
            console.log(err)
        },
    })

    return mutation
}

// post function - add customer / object / employee
export const usePostDataMutation = (queryClient: QueryClient, pageType: string, refreshData: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, PostNewDataParams>({
        mutationFn: postNewDataGeneral,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", pageType + " wird gerade erstellt.")
        },
        onSuccess: () => {
            messageSuccess(notificationKey, "Fertig!", pageType + " wurde erfolgreich erstellt.")
            queryClient.invalidateQueries({
                queryKey: [refreshData],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, pageType + " konnte nicht erstellt werden."))
            console.log(err)
        },
    })

    return mutation
}

// delete function - delete customer / object / employee
export const useDeleteDataMutation = (queryClient: QueryClient, modalCon: any, pageType: string, refreshData: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, DeleteDataParams>({
        mutationFn: deleteDataGeneral,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", pageType + " wird gerade gelöscht.")
        },
        onSuccess: () => {
            messageSuccess(notificationKey, "Fertig!", pageType + " wurde erfolgreich gelöscht.")
            modalCon?.trigger(4)
            queryClient.invalidateQueries({
                queryKey: [refreshData],
            })
        },
        onError: (err: any) => {
            // 400 ohne Begründung: Altsystem-Fall offene Zahlungsanforderungen; sonst die Begründung des Backends anzeigen
            if (err?.response?.status === 400 && !apiDisplayMessage(err)) {
                messageError(notificationKey, "Fehler!", pageType + " konnte nicht gelöscht werden. Es sind noch zugeordnete Zahlungsanforderungen offen.")
            } else {
                messageError(notificationKey, "Fehler!", apiErrorMessage(err, pageType + " konnte nicht gelöscht werden."))
            }
            console.log(err)
        },
    })

    return mutation
}

// archive function - update customer / object / employee
export const useArchiveDataMutation = (queryClient: QueryClient, modalConFunction: () => void, refreshData: string, pageType: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, ArchiveDataParams>({
        mutationFn: archiveDataGeneral,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", pageType + " wird gerade archiviert.")
        },
        onSuccess: () => {
            messageSuccess(notificationKey, "Fertig!", pageType + " wurde erfolgreich archiviert.")
            modalConFunction()
            queryClient.invalidateQueries({ queryKey: [refreshData] })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, pageType + " konnte nicht archiviert werden."))
            console.log(err)
        },
    })

    return mutation
}

// start zip extraction
export const useStartZipExportMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; pageType: string }>({
        mutationFn: startZipExtraction,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der ZIP-Export wird gestartet.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Der ZIP-Export wurde erfolgreich gestartet. Bitte laden Sie umgehend die ZIP-Datei herunter, sobald Sie sie per Mail erhalten haben!", 10)
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der ZIP-Export ist fehlgeschlagen."))
            console.log(err)
        },
    })

    return mutation
}

// restore deleted object / customer
export const useRestoreMutation = (queryClient: QueryClient, pageType: string, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; pageType: string }>({
        mutationFn: restoreData,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", `Wiederherstellung des ${pageType} wird gestartet.`)
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", `Wiederherstellung des ${pageType} war erfolgreich.`)
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, `Wiederherstellung des ${pageType} konnte nicht abgeschlossen werden.`))
            console.log(err)
        },
    })

    return mutation
}

// start zip extraction
export const useStartExcelExportMutation = () => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { collectionType: "bill" | "object" | "customer"; startDate?: string; endDate?: string }>({
        mutationFn: startExcelExport,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Excel-Export wird gestartet.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Der Excel-Export wurde erfolgreich gestartet. Sie erhalten die Datei per Mail.")
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Excel-Export ist fehlgeschlagen."))
            console.log(err)
        },
    })

    return mutation
}

// add folder
export const useAddFolderMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { collectionType: "object" | "customer" | "employee"; id: string; folder: string }>({
        mutationFn: addFolder,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Ordner wird erstellt.")
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
            messageSuccess(notificationKey, "Fertig!", "Der Ordner wurde erfolgreich erstellt.")
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Ordner konnte nicht erstellt werden."))
            console.log(err)
        },
    })

    return mutation
}

// delete folder
export const useDeleteFolderMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { collectionType: "object" | "customer" | "employee"; id: string; folder: string }>({
        mutationFn: deleteFolder,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Der Ordner wird gelöscht.")
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
            messageSuccess(notificationKey, "Fertig!", "Der Ordner wurde erfolgreich gelöscht.")
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Der Ordner konnte nicht gelöscht werden."))
            console.log(err)
        },
    })

    return mutation
}

// upload document(s) to object / customer / employee
export const useAddDocumentToFolderMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; data: any; path: string; type: "object" | "customer" | "employee" }>({
        mutationFn: postNewDocumentInFolder,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Dokument wird gerade hinzugefügt.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Das Dokument wurde erfolgreich hinzugefügt.")
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Dokument konnte nicht hinzugefügt werden."))
            console.log(err)
        },
    })

    return mutation
}

// delete doc-data from object & from s3
export const useDeleteDocMutation = (queryClient: QueryClient, queryKey: string) => {
    const [notificationKey, setnotificationKey] = useState<string>("")
    const mutation = useMutation<unknown, Error, { id: string; file: string; type: "object" | "customer" | "employee" }>({
        mutationFn: deleteDocFromSingleType,
        onMutate: () => {
            const newKey = Math.random().toString(36).substring(2, 12)
            setnotificationKey(newKey)
            messagePending(newKey, "In Bearbeitung...", "Das Dokument wird gerade gelöscht.")
        },
        onSuccess: (data, variables) => {
            messageSuccess(notificationKey, "Fertig!", "Das Dokument wurde erfolgreich gelöscht.")
            queryClient.invalidateQueries({
                queryKey: [queryKey],
            })
        },
        onError: (err) => {
            messageError(notificationKey, "Fehler!", apiErrorMessage(err, "Das Dokument konnte nicht gelöscht werden."))
            console.log(err)
        },
    })

    return mutation
}
